import React, { useMemo } from "react";
import LogisticsCompany from "@/enum/LogisticsCompany";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { EwaybillStatus } from "@/services/api/details";
import { Button, DatePicker, Form, FormInstance, Radio, Row, Select } from "antd";
import useProviderEwaybillAccountOptions from "../SharedEwaybillAccount/Provider/hooks/useProviderEwaybillAccountOptions";
import moment, { Moment } from "moment";
import { useRequest } from "ahooks";
import { ConsumerInfo, getConsumerInfos } from "@/services/api/sharedEwaybillShippingAddress";
import { useHistory } from "react-router-dom";

const range7Day = [moment().add(-7, "day"), moment()];
const range30Day = [moment().add(-30, "day"), moment()];
const range3Month = [moment().add(-3, "month"), moment()];

enum RangeDateType {
  range7Day = "range7Day",
  range30Day = "range30Day",
  range3Month = "range3Month",
}

const RangeDateTypeMapping = {
  [RangeDateType.range7Day]: range7Day,
  [RangeDateType.range30Day]: range30Day,
  [RangeDateType.range3Month]: range3Month,
};

function getCurrentRangeDateType(range?: Moment[]): RangeDateType | undefined {
  const startDate = range?.[0].format("YYYY-MM-DD");
  const endDate = range?.[1].format("YYYY-MM-DD");

  for (const key in RangeDateTypeMapping) {
    if (Object.prototype.hasOwnProperty.call(RangeDateTypeMapping, key)) {
      const range = RangeDateTypeMapping[key];
      const start = range[0].format("YYYY-MM-DD");
      const end = range[1].format("YYYY-MM-DD");

      if (start === startDate && end === endDate) {
        return key as RangeDateType;
      }
    }
  }

  return undefined;
}

interface SearchBarProps {
  form?: FormInstance<FormValues>;

  onSearch?(): void;

  onReset?(): void;

  onExport?(): void;

  consumerInfos: ConsumerInfo[];
}

export interface FormValues {
  ewaybillAccountId?: number;
  logisticsCompany?: LogisticsCompany;
  status?: EwaybillStatus;
  consumerId?: number;
  rangeDate?: [Moment, Moment];
}

interface ExportButtonProps {
  onExport: () => void;
}

const ExportButton: React.FC<ExportButtonProps> = React.memo(function (props) {
  const { onExport } = props;

  return (
    <Button type="primary" onClick={onExport}>
      导出明细
    </Button>
  );
});

const SearchBar: React.FC<SearchBarProps> = function (props) {
  const { form: formProps, onReset, onSearch, onExport, consumerInfos } = props;

  const history = useHistory();

  const [form] = Form.useForm<FormValues>(formProps);

  const { loading, ewaybillAccountOptions } = useProviderEwaybillAccountOptions();

  return (
    <Form
      form={form}
      style={{ padding: "20px 10px 20px 20px" }}
      initialValues={{
        rangeDate: RangeDateTypeMapping[RangeDateType.range7Day],
      }}
    >
      <Row>
        <Form.Item label="面单账号" name="ewaybillAccountId" className="mr10">
          <Select
            placeholder="全部"
            style={{ width: 156 }}
            options={ewaybillAccountOptions}
            allowClear
            loading={loading}
            showSearch
            dropdownMatchSelectWidth={false}
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item label="物流公司" name="logisticsCompany" className="mr10">
          <Select placeholder="全部快递" style={{ width: 156 }} allowClear showSearch optionFilterProp="children">
            <Select.Option value={undefined}>全部</Select.Option>
            {Object.values(LogisticsCompany).map((logisticsCompany) => {
              return (
                <Select.Option key={logisticsCompany} value={logisticsCompany}>
                  {LogisticsCompanyNameMap[logisticsCompany]}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item label="单号状态" name="status" className="mr10">
          <Select placeholder="全部" style={{ width: 156 }} allowClear showSearch optionFilterProp="children">
            <Select.Option value={undefined}>全部</Select.Option>
            <Select.Option value={EwaybillStatus.NORMAL}>有效单号</Select.Option>
            <Select.Option value={EwaybillStatus.CANCELED}>已取消单号</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label="使用面单的人" name="consumerId">
          <Select placeholder="全部" style={{ width: 156 }} allowClear showSearch optionFilterProp="children">
            {consumerInfos?.map((it) => {
              return (
                <Select.Option key={it.id} value={it.id}>
                  {it.consumerName}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
      </Row>
      <Row>
        <Form.Item label="时间" shouldUpdate={(prev, next) => prev.rangeDate !== next.rangeDate}>
          {(form) => {
            const rangeDate: Moment[] | undefined = form.getFieldValue("rangeDate");

            const value = getCurrentRangeDateType(rangeDate);

            return (
              <Radio.Group
                value={value}
                onChange={(e) => {
                  const type = e.target.value as RangeDateType;

                  const range = RangeDateTypeMapping[type];

                  form.setFieldsValue({
                    rangeDate: range,
                  });
                }}
              >
                <Radio.Button value={RangeDateType.range7Day}>近7天</Radio.Button>
                <Radio.Button value={RangeDateType.range30Day}>近30天</Radio.Button>
                <Radio.Button value={RangeDateType.range3Month}>近3个月</Radio.Button>
              </Radio.Group>
            );
          }}
        </Form.Item>

        <Form.Item name="rangeDate">
          <DatePicker.RangePicker
            className="ml16"
            disabledDate={(current) => {
              return (
                current >
                  moment()
                    .add(+1, "days")
                    .endOf("day") || current < moment().add(-180, "days").endOf("day")
              );
            }}
          />
        </Form.Item>

        <Button type="primary" className="ml16" onClick={onSearch}>
          查询
        </Button>
        <Button type="default" className="ml16" onClick={onReset}>
          重置
        </Button>
      </Row>

      <Row>
        <ExportButton onExport={onExport} />
        <span className="ml10" style={{ lineHeight: "32px", color: "#999" }}>
          仅可导出近180天的明细
        </span>
        <Button
          type="default"
          className="ml16"
          onClick={() => {
            history.push("/shareEwaybillToOthers/details/exportEwaybillHistory");
          }}
        >
          导出记录
        </Button>
      </Row>
    </Form>
  );
};

export default React.memo(SearchBar);
